import React, { useState, useEffect, useMemo } from 'react';
import { 
  Paper, Typography, Stack, Box, CircularProgress, Alert, 
  Button, Switch, FormControlLabel, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { loadDicomPreview } from '../../services/dicomService';

const DicomPreviewPanel = ({ activeFile, isProcessing }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [previewData, setPreviewData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewAsVideo, setPreviewAsVideo] = useState(false);
  const [error, setError] = useState('');

  const [metadataOpen, setMetadataOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchPreview = async () => {
      if (!activeFile?.file) {
        setPreviewData(null);
        setError('');
        return;
      }

      setIsPreviewLoading(true);
      setError('');
      setPreviewData(null);

      const mode = previewAsVideo ? 'video' : 'first_frame';
      const res = await loadDicomPreview(activeFile.file, mode);
      
      if (res.success) {
        setPreviewData(res.data);
      } else {
        setError(res.error || t('DicomPage.previewError', 'Nepodarilo sa načítať náhľad DICOM súboru.'));
      }
      setIsPreviewLoading(false);
    };

    fetchPreview();
  }, [activeFile?.file, previewAsVideo, t]);

  // Update filter logic to search through Keyword, Name, AND Value
  const filteredMetadata = useMemo(() => {
    if (!previewData?.metadata) return [];
    
    const query = searchQuery.toLowerCase();
    return Object.entries(previewData.metadata).filter(([keyword, data]) => 
      keyword.toLowerCase().includes(query) || 
      data.name.toLowerCase().includes(query) ||
      String(data.value).toLowerCase().includes(query)
    );
  }, [previewData, searchQuery]);

  const handleCopyKeyword = (keyword) => {
    navigator.clipboard.writeText(keyword);
    enqueueSnackbar(t('PseudoDicomPage.keywordCopied', `Tag "${keyword}" bol skopírovaný.`), { variant: 'success', autoHideDuration: 2000 });
  };

  return (
    <>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          height: '100%'
        }}
      >
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <VisibilityIcon color="primary" /> {t('PseudoDicomPage.previewTitle', 'Náhľad dát')}
            {activeFile && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
                ({activeFile.name})
              </Typography>
            )}
          </Typography>
          
          <FormControlLabel
            control={
              <Switch 
                checked={previewAsVideo} 
                onChange={(e) => setPreviewAsVideo(e.target.checked)} 
                disabled={!activeFile || isPreviewLoading || isProcessing}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('PseudoDicomPage.videoToggle', 'Ako video')}
              </Typography>
            }
          />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {isPreviewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : previewData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
            
            {previewData.preview?.data ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  bgcolor: 'black', 
                  borderRadius: 2, 
                  overflow: 'hidden', 
                  border: '1px solid', 
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                  minHeight: 400
                }}
              >
                <img 
                  src={previewData.preview.data} 
                  alt="DICOM Preview" 
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', display: 'block', margin: '0 auto' }} 
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t('PseudoDicomPage.noImage', 'Súbor neobsahuje obrazové dáta.')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<InfoOutlinedIcon />}
                onClick={() => setMetadataOpen(true)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {t('PseudoDicomPage.viewMetadata', 'Zobraziť technické metadáta')}
              </Button>
            </Box>

          </Box>
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 400, 
              flexGrow: 1,
              color: 'text.secondary', 
              bgcolor: 'action.hover', 
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider'
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>
              {t('PseudoDicomPage.emptyPreview', 'Vyberte súbor zo zoznamu pre zobrazenie náhľadu.')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Metadata Dialog */}
      <Dialog 
        open={metadataOpen} 
        onClose={() => setMetadataOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('PseudoDicomPage.metadataTitle', 'DICOM Metadáta')}
          </Typography>
          <IconButton onClick={() => setMetadataOpen(false)} size="small" sx={{ bgcolor: 'action.hover' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <Box sx={{ px: 2, pb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('PseudoDicomPage.searchMetadata', 'Vyhľadať DICOM Keyword, Názov alebo hodnotu...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2 },
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
          />
        </Box>

        <DialogContent dividers sx={{ p: 0, bgcolor: 'background.default' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '45%', bgcolor: 'action.hover', borderRight: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                    DICOM Keyword / Názov
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'action.hover', fontWeight: 600 }}>
                    Hodnota (Value)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMetadata.length > 0 ? (
                  filteredMetadata.map(([keyword, data]) => (
                    <TableRow key={keyword} hover sx={{ bgcolor: 'background.paper' }}>
                      <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {keyword}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {data.name}
                            </Typography>
                          </Box>
                          <Tooltip title={t('common.copy', 'Kopírovať Keyword')}>
                            <IconButton size="small" onClick={() => handleCopyKeyword(keyword)} sx={{ opacity: 0.5, '&:hover': { opacity: 1, bgcolor: 'action.hover' } }}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {data.value}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ bgcolor: 'background.paper' }}>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary', fontWeight: 500 }}>
                      {t('PseudoDicomPage.noMetadataFound', 'Nenašli sa žiadne zhody.')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, fontWeight: 500 }}>
            {t('PseudoDicomPage.totalTags', 'Celkom tagov:')} {Object.keys(previewData?.metadata || {}).length}
          </Typography>
          <Button 
            onClick={() => setMetadataOpen(false)} 
            variant="contained" 
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('common.close', 'Zavrieť')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DicomPreviewPanel;